import {
  principalSchema,
  parseContext,
  testSelectionSchema,
  type SourceParameters,
  type TestSelection,
  type Principal,
  type Values,
} from "@proof/core";
import { z } from "zod";

const marker = "proof-native-preview-v1";
const noncePattern = /^[a-f0-9]{48}$/;
export function previewWindowFeatures() {
  const width = Math.min(
    4096,
    Math.max(320, Number(window.screen?.availWidth) || 1200),
  );
  const height = Math.min(
    4096,
    Math.max(320, Number(window.screen?.availHeight) || 800),
  );
  return `popup=yes,width=${Math.round(width)},height=${Math.round(height)}`;
}
const value = z.union([
  z.string(),
  z.number().finite(),
  z.boolean(),
  z.array(z.string()),
  z.record(z.string(), z.json()),
  z.null(),
]);
const seedSchema = z
  .strictObject({
    previewSubject: z.string().min(1).max(200).nullable().optional(),
    sourceParameters: z
      .unknown()
      .optional()
      .transform((value) =>
        value === undefined
          ? undefined
          : parseContext({ sourceParameters: value }).sourceParameters,
      ),
    projectId: z.string().min(1).max(200),
    versionId: z.string().min(1).max(200).optional(),
    /** Drafts or a mirrored form; the preview resolves it with the server's authorization. */
    testSelection: testSelectionSchema.optional(),
    partId: z.string().max(200),
    values: z.record(z.string(), value),
  })
  .refine((seed) => !!seed.versionId !== !!seed.testSelection, {
    message: "Choose a published version or a test selection.",
  });
export interface NativePreviewSeed {
  previewSubject?: string | null;
  sourceParameters?: SourceParameters;
  projectId: string;
  versionId?: string;
  testSelection?: TestSelection;
  partId: string;
  values: Values;
}
export const previewIdentityKey = (principal: Principal) =>
  JSON.stringify([
    principal.tenantId,
    principal.subject,
    principal.revision,
    principal.authorization,
    principal.operatorContext,
  ]);
export function parsePreviewSeed(input: unknown): NativePreviewSeed {
  const serialized = JSON.stringify(input, (_key, entry) => {
    if (
      ["function", "symbol", "bigint"].includes(typeof entry) ||
      (typeof entry === "number" && !Number.isFinite(entry))
    )
      throw new Error("Preview values must use JSON-compatible data.");
    return entry;
  });
  if (serialized === undefined) throw new Error("Invalid preview values.");
  if (new TextEncoder().encode(serialized).length > 1_000_000)
    throw new Error("Preview values exceed the supported size.");
  // Match API serialization: cleared optional image settings are omitted, not persisted as overrides.
  return seedSchema.parse(JSON.parse(serialized));
}

/** One-use handoff to the exact opened window. No credentials or working values enter storage or URLs. */
export function openNativePreview(options: {
  principal: Principal;
  token?: string;
  seed: NativePreviewSeed;
  signal: AbortSignal;
  onClose?: () => void;
  newWindow?: boolean;
}) {
  if (options.signal.aborted) throw new Error("Workspace closed");
  const principal = principalSchema.parse(options.principal);
  if (!principal.authorization?.capabilities.includes("tenant.proofs.use"))
    throw new Error("Proof preview is not available for this account.");
  const seed = parsePreviewSeed(options.seed),
    nonce = Array.from(crypto.getRandomValues(new Uint8Array(24)), (b) =>
      b.toString(16).padStart(2, "0"),
    ).join("");
  const popup = options.newWindow
    ? window.open(`/proof-preview#${nonce}`, "_blank", previewWindowFeatures())
    : window.open(`/proof-preview#${nonce}`, "_blank");
  if (!popup) throw new Error("Allow popups to open the proof preview.");
  let port: MessagePort | undefined,
    closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    try {
      port?.postMessage({ type: "ended" });
    } catch {
      /* The child may already have closed. */
    }
    port?.close();
    clearTimeout(expiry);
    clearInterval(heartbeat);
    window.removeEventListener("message", ready);
    window.removeEventListener("pagehide", close);
    options.signal.removeEventListener("abort", close);
    options.onClose?.();
  };
  const ready = (event: MessageEvent) => {
    if (
      closed ||
      port ||
      event.origin !== location.origin ||
      event.source !== popup ||
      event.data?.type !== marker ||
      event.data?.nonce !== nonce ||
      event.ports.length !== 1
    )
      return;
    // Match both the source window and the expected same-origin route before releasing a credential.
    try {
      if (
        popup.location.pathname !== "/proof-preview" ||
        popup.location.hash !== `#${nonce}`
      )
        return;
    } catch {
      return;
    }
    port = event.ports[0];
    window.removeEventListener("message", ready);
    clearTimeout(expiry);
    port.onmessage = (message) => {
      if (message.data?.type === "close") close();
    };
    port.onmessageerror = close;
    port.start();
    port.postMessage({ type: "grant", principal, token: options.token, seed });
  };
  const expiry = setTimeout(close, 45_000),
    heartbeat = setInterval(() => {
      if (popup.closed) close();
      else if (port) {
        try {
          port.postMessage({ type: "heartbeat" });
        } catch {
          close();
        }
      }
    }, 5000);
  window.addEventListener("message", ready);
  window.addEventListener("pagehide", close);
  options.signal.addEventListener("abort", close, { once: true });
  return { close };
}

export interface NativePreviewGrant {
  principal: Principal;
  token?: string;
  seed: NativePreviewSeed;
  close: () => void;
}
/** Reloads and copied URLs have no reusable credential. A lost opener heartbeat also ends access. */
export function receiveNativePreview(
  signal: AbortSignal,
  onEnded: () => void,
): Promise<NativePreviewGrant> {
  return new Promise((resolve, reject) => {
    const nonce = location.hash.slice(1),
      opener = window.opener;
    if (signal.aborted || !noncePattern.test(nonce) || !opener) {
      reject(new Error("Open a new proof preview from Live proof."));
      return;
    }
    const { port1: port, port2 } = new MessageChannel();
    let settled = false,
      closed = false,
      lastHeartbeat = Date.now();
    const close = () => {
      if (closed) return;
      closed = true;
      clearTimeout(expiry);
      clearInterval(watchdog);
      signal.removeEventListener("abort", abort);
      window.removeEventListener("pagehide", end);
      try {
        port.postMessage({ type: "close" });
      } catch {
        /* Disconnected parent. */
      }
      port.close();
    };
    const end = () => {
      if (closed) return;
      close();
      if (settled) onEnded();
      else reject(new Error("This proof preview connection has expired."));
    };
    const abort = () => {
      close();
      if (!settled) reject(new DOMException("Preview closed", "AbortError"));
    };
    const expiry = setTimeout(end, 10_000),
      watchdog = setInterval(() => {
        if (Date.now() - lastHeartbeat > 15_000) end();
      }, 1000);
    port.onmessageerror = end;
    port.onmessage = (event) => {
      if (closed) return;
      if (event.data?.type === "ended") {
        end();
        return;
      }
      if (event.data?.type === "heartbeat") {
        lastHeartbeat = Date.now();
        return;
      }
      if (settled || event.data?.type !== "grant") return;
      try {
        const principal = principalSchema.parse(event.data.principal),
          seed = parsePreviewSeed(event.data.seed),
          token = event.data.token;
        if (
          !principal.authorization?.capabilities.includes(
            "tenant.proofs.use",
          ) ||
          (token !== undefined &&
            (typeof token !== "string" ||
              !token ||
              token.length > 4096 ||
              /[^\x21-\x7e]/.test(token)))
        )
          throw new Error();
        settled = true;
        clearTimeout(expiry);
        lastHeartbeat = Date.now();
        resolve({ principal, seed, token, close });
      } catch {
        end();
      }
    };
    port.start();
    signal.addEventListener("abort", abort, { once: true });
    window.addEventListener("pagehide", end);
    try {
      opener.postMessage({ type: marker, nonce }, location.origin, [port2]);
      window.opener = null;
    } catch {
      port2.close();
      end();
    }
  });
}
